import React, { useEffect, useState } from 'react';
import {
    EuiText,
    EuiPanel,
    EuiSelect,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFieldText,
    EuiFormRow,
    EuiTextAlign,
    EuiButton,
    EuiSpacer,
  } from '@elastic/eui';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import '../style.css';

const ROLE_ENUM = ['User', 'Admin', 'Deleted'];

// 后端返回数字枚举 (0/1/2)，或字符串 ("User"|"Admin"|"Deleted")
function roleToString(role) {
  if (typeof role === 'number') return ROLE_ENUM[role] || 'User';
  if (typeof role === 'string') {
    const match = ROLE_ENUM.find(r => r.toLowerCase() === role.toLowerCase());
    return match || 'User';
  }
  return 'User';
}

// 前端提交时，把字符串转成后端的数字枚举
function roleStringToNumber(role) {
  const idx = ROLE_ENUM.indexOf(role);
  return idx >= 0 ? idx : 0;
}

const ROLE_OPTIONS = [
  { value: 'User', text: 'User' },
  { value: 'Admin', text: 'Admin' },
  { value: 'Deleted', text: 'Deleted' },
];

const UserManagement = () => {
  const [users, setUsers] = useState([]); // 用于存储用户列表
  const [selectedUser, setSelectedUser] = useState(null); // 用于存储选中的用户信息
  const [errorMessage, setErrorMessage] = useState(''); // 用于显示错误信息
  const [loading, setLoading] = useState(true); // 用于加载状态
  const [reloadKey, setReloadKey] = useState(0);
  const [sortKey, setSortKey] = useState('id'); // 用于存储排序方式
  const [searchTerm, setSearchTerm] = useState(''); // 用于存储搜索关键词
  const [totalCount, setTotalCount] = useState(0);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylistSongs, setSelectedPlaylistSongs] = useState({});
  const [expandedPlaylistId, setExpandedPlaylistId] = useState(null);  // 存储当前展开的歌单ID

    // 角色编辑相关
  const [editRole, setEditRole] = useState('');           // 当前编辑中的角色值
  const [savingRole, setSavingRole] = useState(false);    // 保存中

  // 当前登录管理员信息
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('');

  // 加载用户列表
  useEffect(() => {
    const token = sessionStorage.getItem("token");  // 从 localStorage 中获取 token
    let userRole = "";
    let userId = null;

    // if (token) {
    //     const decodedToken = jwtDecode(token);  // 解码 token
    //     // console.log(decodedToken);  // 检查 token 中的内容，包括角色信息
    //     userRole = decodedToken.role;  // 获取角色信息
    //     userId = decodedToken.UserId || decodedToken.userId || decodedToken.sub;
    // }
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        // 你的后端生成的 token 中，role 是字符串 "Admin" | "User"
        userRole = decodedToken.role || decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
        // UserId claim（后端在 GenerateJwtToken 时写入了 "UserId"）
        userId = decodedToken.UserId || decodedToken.userId || decodedToken.sub;
      } catch (e) {
        console.warn("Failed to decode token", e);
      }
    }

    setCurrentUserRole(userRole);
    setCurrentUserId(userId ? Number(userId) : null);

    // 只有管理员才能获取用户列表
    if (userRole !== 'Admin') {
      setErrorMessage('Access denied. Only admins can view this page.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    const controller = new AbortController();

    const loadUsers = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await axios.get('/api/users', { signal: controller.signal });
          if (controller.signal.aborted) return;
          setUsers(response.data);
          setTotalCount(response.data.length);
          setLoading(false);
          return;
        } catch (error) {
          if (controller.signal.aborted || axios.isCancel(error) || error.code === 'ERR_CANCELED') return;
          if (!error.response && attempt === 0) {
            await new Promise(resolve => setTimeout(resolve, 350));
            continue;
          }
          console.error('Error fetching users:', error);
          setErrorMessage('載入用戶失敗，請重新載入。');
          setLoading(false);
          return;
        }
      }
    };

    loadUsers();
    return () => controller.abort();
  }, [reloadKey]);

  // 点击某个用户时显示其详细信息
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setEditRole(roleToString(user.role));   // 进入详情时带入当前角色
    setExpandedPlaylistId(null);   // 折叠歌单
    setSelectedPlaylistSongs({});  // 清空歌曲
    setPlaylists([]);
  };

  // 根据搜索关键词过滤列表
  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 对过滤后的用户列表进行排序
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortKey === 'id') {
        return a.id - b.id;
    } else if (sortKey === 'username') {
        return a.username.localeCompare(b.username);
    }
    return 0;
  });

  if (loading) {
    return <p>Loading...</p>;  // 显示加载中的提示
  }

  if (errorMessage) {
    return (
      <EuiPanel paddingSize="m">
        <p>{errorMessage}</p>
        <EuiButton onClick={() => setReloadKey(current => current + 1)}>重新載入</EuiButton>
      </EuiPanel>
    );
  }

  const handleViewPlaylists = () => {
    axios.get(`/api/playlists/admin/user-playlists`, { params: { userId: selectedUser.id , type: 'song'} })
      .then(response => {
        setPlaylists(response.data);  // 获取用户所有歌单
      })
      .catch(error => {
        console.error('获取歌单失败:', error);
      });
  };
  
  const handleToggleSongs = (playlistId) => {
    if (playlistId === expandedPlaylistId) {
      setExpandedPlaylistId(null);  // 如果再次点击已展开的歌单，折叠
    } else {
      setExpandedPlaylistId(playlistId);  // 展开新的歌单
      // 如果诗歌列表还没有加载，则加载诗歌
      if (!selectedPlaylistSongs[playlistId]) {
        axios.get(`/api/playlists/${playlistId}/songs`)
          .then(response => {
            setSelectedPlaylistSongs(prevSongs => ({
              ...prevSongs,
              [playlistId]: response.data
            }));
          })
          .catch(error => {
            console.error('加载诗歌失败:', error);
          });
      }
    }
  };  

  // 保存角色
  const handleSaveRole = async () => {
    if (!selectedUser) return;
    if (editRole === selectedUser.role) return; // 无改动
    const token = sessionStorage.getItem("token");

    // 前端保护：管理员不能把自己设为 Deleted
    if (currentUserRole === 'Admin' &&
        currentUserId != null &&
        Number(selectedUser.id) === Number(currentUserId) &&
        editRole === 'Deleted') {
      alert('不允許管理員將自己標記為刪除。');
      setEditRole(selectedUser.role); // 还原
      return;
    }

    // 风险操作确认
    if (editRole === 'Deleted') {
      const ok = window.confirm(`確定將用戶「${selectedUser.username}」標記為 Deleted 嗎？此操作將阻止該用戶登入。`);
      if (!ok) return;
    }

    try {
      setSavingRole(true);
      await axios.put(
        `/api/users/${selectedUser.id}/role`,
        { role: roleStringToNumber(editRole) }, // body：{ role: "User" | "Admin" | "Deleted" }
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          }
        }
      );

      // 同步本地列表与详情
      setUsers(prev =>
        prev.map(u => u.id === selectedUser.id ? { ...u, role: editRole } : u)
      );
      setSelectedUser(prev => prev ? { ...prev, role: editRole } : prev);

      alert('角色已更新');
    } catch (err) {
      console.error('更新角色失敗：', err);
      // 从后端取 message
      let msg = '更新角色失敗，請稍後再試。';
      if (err.response && err.response.data) {
        // 常见几种情况：
        if (typeof err.response.data === 'string') {
          msg = err.response.data;
        } else if (err.response.data.message) {
          msg = err.response.data.message;
        } else if (err.response.data.details) {
          msg = err.response.data.details;
        }
      }
      alert(`更新角色失敗：${msg}`);
      // 失败则回滚选择框值
      setEditRole(selectedUser.role);
    } finally {
      setSavingRole(false);
    }
  };

  return (
    <EuiFlexGroup>
    {/* 左侧用户列表 */}
    <EuiFlexItem grow={false} className="song-list-container">
      <EuiPanel paddingSize="s">
        <EuiFormRow label={`用户列表 共${totalCount}個`}>
          <EuiSelect
            options={[
              { value: 'username', text: '按名称排序' },
              { value: 'id', text: '按ID排序' }
            ]}
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          />
        </EuiFormRow>

        {/* 搜索框 */}
        <input
            type="text"
            placeholder="搜索用戶..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
          />

        <ul className="song-list">
            {sortedUsers.map((user) => (
              <li key={user.id} onClick={() => handleUserClick(user)}>
                {user.username} (ID: {user.id})
              </li>
            ))}
        </ul>
      </EuiPanel>
    </EuiFlexItem>

    {/* 右侧详细信息编辑 */}
    <EuiFlexItem>
    {selectedUser ? (
        <EuiPanel paddingSize="l">
        <EuiTextAlign textAlign="center">
            <h3>查看用户: {selectedUser.username}</h3>
        </EuiTextAlign>

        <EuiFlexGroup>
            {/* 左列 */}
            <EuiFlexItem>
            <EuiFormRow label="User ID">
                <EuiFieldText value={selectedUser.id} readOnly />
            </EuiFormRow>

            <EuiFormRow label="User name">
                <EuiFieldText
                value={selectedUser.username} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Email">
                <EuiFieldText
                value={selectedUser.email} readOnly
                />
            </EuiFormRow>

            {/* 角色（可编辑） */}
            <EuiFormRow label="Role">
              <EuiSelect
                options={ROLE_OPTIONS}
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                aria-label="select-role"
              />
            </EuiFormRow>

            <EuiButton
              onClick={handleSaveRole}
              isLoading={savingRole}
              isDisabled={!selectedUser || editRole === selectedUser.role}
            >
              保存角色
            </EuiButton>

            <EuiSpacer size="m" />

            <EuiFormRow label="Verification Code">
                <EuiFieldText
                    value={selectedUser.verificationCode || ''} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Code Generated At">
                <EuiFieldText
                    value={selectedUser.codeGeneratedAt || ''} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Registration Time">
                <EuiFieldText
                    value={selectedUser.registrationTime || ''} readOnly
                />
            </EuiFormRow> 
            </EuiFlexItem>

            {/* 右列 */}
            <EuiFlexItem>
            <EuiFormRow label="Reset Password Token">
                <EuiFieldText
                    value={selectedUser.resetPasswordToken || ''} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Reset Password Token Expiry">
                <EuiFieldText
                    value={selectedUser.resetPasswordTokenExpiry || ''} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Channel">
                <EuiFieldText
                    value={selectedUser.channel || ''} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Language">
                <EuiFieldText
                    value={selectedUser.language || ''} readOnly
                />
            </EuiFormRow>

            <EuiFormRow label="Reading Plan">
                <EuiFieldText
                    value={selectedUser.readingPlan || ''} readOnly
                />
            </EuiFormRow>    
            
            <EuiFormRow label="Last Login Time">
                <EuiFieldText
                    value={selectedUser.lastLoginTime || ''} readOnly
                />
            </EuiFormRow> 

            <EuiFormRow label="Last Login IP">
                <EuiFieldText
                    value={selectedUser.lastLoginIP || ''} readOnly
                />
            </EuiFormRow> 
            </EuiFlexItem>
        </EuiFlexGroup>
            <ul>
              {playlists.map(playlist => (
                <li key={playlist.id}>
                  {/* 歌单名称变为可点击 */}
                  <span onClick={() => handleToggleSongs(playlist.id)} style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}>
                    {playlist.name}
                  </span>

                  {/* 如果当前歌单是展开状态，则显示诗歌列表 */}
                  {expandedPlaylistId === playlist.id && (
                    <ul>
                      {selectedPlaylistSongs[playlist.id] ? (
                        selectedPlaylistSongs[playlist.id].map(song => (
                          <li key={song.resourceid}>{song.name}  ({song.engineID})</li>
                        ))
                      ) : (
                        <li>加载中...</li>  // 还没有加载诗歌时显示的内容
                      )}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <div style={{ textAlign: 'center' }}>
              <EuiButton onClick={handleViewPlaylists}>查询用户歌单</EuiButton>
            </div>
        </EuiPanel>
    ) : (
        <EuiText>请选择一个用户以查看详细信息。</EuiText>
    )}
    </EuiFlexItem>
    </EuiFlexGroup>
)};

export default UserManagement;